import { Suspense, forwardRef, lazy } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import Loader from 'components/shared/Loader/Loader';
import ErrorMessage from 'components/shared/ErrorMessage/ErrorMessage';

export const SectionFallback = () => (
  <div className="flex h-full w-full items-center justify-center">
    <Loader color="primary" />
  </div>
);

const CHUNK_RELOAD_KEY = 'chunkLoadReloadVersion';

const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' || /Loading chunk .* failed/i.test(error?.message || '');

const recoverFromChunkError = (error) => {
  if (!isChunkLoadError(error)) return;
  const version = String(window.__APP_VERSION__ || '');
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === version) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, version);
  window.location.reload();
};

export const withFallback = (Comp, Fallback = SectionFallback, ErrorComp = ErrorMessage) =>
  function WithFallback(props) {
    return (
      <ErrorBoundary
        FallbackComponent={ErrorComp}
        onError={recoverFromChunkError}
        onReset={() => window.location.reload()}>
        <Suspense fallback={<Fallback />}>
          <Comp {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  };

async function clearChunkFromCache(url) {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    for (const name of cacheNames) {
      const cache = await caches.open(name);
      await cache.delete(url);
    }
  }
}

export function retryLazy(factory, retries = 2, delay = 1000) {
  return new Promise((resolve, reject) => {
    factory()
      .then(resolve)
      .catch(async (err) => {
        const match = err?.message?.match(/Loading chunk [\d]+ failed.*\((.*)\)/);
        const failedUrl = match?.[1];

        if (failedUrl) {
          await clearChunkFromCache(failedUrl);
        }

        if (retries === 0) return reject(err);
        setTimeout(() => {
          retryLazy(factory, retries - 1, delay)
            .then(resolve)
            .catch(reject);
        }, delay);
      });
  });
}

export const withLazyFallback = (factory, Fallback = SectionFallback, ErrorComp = ErrorMessage) => {
  const LazyComp = lazy(() => retryLazy(factory, 2));
  const Wrapped = forwardRef((props, ref) => (
    <ErrorBoundary
      FallbackComponent={ErrorComp}
      onError={recoverFromChunkError}
      onReset={() => window.location.reload()}>
      <Suspense fallback={<Fallback />}>
        <LazyComp {...props} ref={ref} />
      </Suspense>
    </ErrorBoundary>
  ));
  return Wrapped;
};
